import { useBusinessDocuments } from '@/components/InvoiceReportDetailDrawer/hooks/useBusinessDocuments';
import { reportFormInputType } from 'ap-openapi';
import {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import type {
  ActiveFileId,
  FormInputItem,
  PaymentInfoData,
  ProcessedBusinessDocument,
} from '../types';

type BusinessDocumentsContextType = {
  data: PaymentInfoData | undefined;
  setData: (data: PaymentInfoData | undefined) => void;
  processedDocuments: ProcessedBusinessDocument[];
  activeFile: ActiveFileId;
  selectDocumentFile: (document: ProcessedBusinessDocument) => () => void;
  isVisible: (item: FormInputItem) => boolean;
};

const Context = createContext<BusinessDocumentsContextType>({
  data: undefined,
  setData: () => {},
  processedDocuments: [],
  activeFile: null,
  selectDocumentFile: () => () => {},
  isVisible: () => true,
});

export const BusinessDocumentsProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const baseURL = location.origin;
  const [data, setData] = useState<PaymentInfoData | undefined>(undefined);

  // Find the business documents field
  const businessDocsField = useMemo(
    () =>
      data?.report_form_inputs?.find(
        (item) =>
          item?.type ===
          reportFormInputType.ApReportFormInputBusinessDocumentsField
      ),
    [data?.report_form_inputs]
  );

  // Get processed documents
  const { processedDocuments, activeFile, selectDocumentFile } =
    useBusinessDocuments(businessDocsField, baseURL);

  const value = useMemo(
    () => ({
      data,
      setData,
      processedDocuments,
      activeFile,
      selectDocumentFile,
      isVisible: (item: FormInputItem) =>
        !(
          item?.type ===
            reportFormInputType.ApReportFormInputBusinessDocumentsField &&
          !processedDocuments?.length
        ),
    }),
    [data, processedDocuments, activeFile, selectDocumentFile]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useBusinessDocumentsContext = () => useContext(Context);
